import React, { useState, useEffect } from 'react';
import { Server, Plus, Edit2, Trash2, CheckCircle, XCircle, Globe, Info } from 'lucide-react';
import axios from 'axios';
import './AdminServers.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Create axios instance with auth
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  // Try JWT token first
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (window.Telegram?.WebApp?.initData) {
    // Fallback to TMA authentication
    const initData = window.Telegram.WebApp.initData;
    config.headers.Authorization = `tma ${initData}`;
  }
  return config;
});

function AdminServers() {
  const [servers, setServers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [validationResult, setValidationResult] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    domain: '',
    name: '',
    country_code: '',
    subscription_base_url: '',
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load servers using axios
      const serversRes = await api.get('/admin/servers/list', {
        params: { include_inactive: true }
      });
      setServers(Array.isArray(serversRes.data) ? serversRes.data : []);

      // Load countries
      const countriesRes = await api.get('/admin/servers/countries');
      setCountries(Array.isArray(countriesRes.data) ? countriesRes.data : []);
    } catch (error) {
      console.error('Failed to load data:', error);
      setServers([]);
      setCountries([]);
      const errorMessage = error.response?.data?.detail || error.message;
      window.Telegram?.WebApp?.showAlert('Ошибка загрузки данных: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const validateSubscriptionUrl = async (url) => {
    if (!url) return;

    try {
      const res = await api.post('/admin/servers/validate-subscription', null, {
        params: { url }
      });
      setValidationResult(res.data);
    } catch (error) {
      console.error('Validation error:', error);
      setValidationResult({ valid: false, error: error.message });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingServer) {
        await api.patch(`/admin/servers/${editingServer.id}`, formData);
        window.Telegram?.WebApp?.showAlert('Сервер обновлен!');
      } else {
        await api.post('/admin/servers/create', formData);
        window.Telegram?.WebApp?.showAlert('Сервер добавлен!');
      }

      // Reset form
      setFormData({
        domain: '',
        name: '',
        country_code: '',
        subscription_base_url: '',
        is_active: true
      });
      setShowAddForm(false);
      setEditingServer(null);
      setValidationResult(null);

      // Reload data
      loadData();

      // TODO: Send notification to all users about new server
      if (!editingServer && formData.is_active) {
        await notifyUsersAboutNewServer();
      }
    } catch (error) {
      console.error('Save error:', error);
      window.Telegram?.WebApp?.showAlert(`Ошибка: ${error.message}`);
    }
  };

  const notifyUsersAboutNewServer = async () => {
    // This will be implemented in the bot
    console.log('Sending notifications about new server...');
  };

  const handleDelete = async (serverId) => {
    if (!window.confirm('Удалить этот сервер?')) return;

    try {
      await api.delete(`/admin/servers/${serverId}`);
      window.Telegram?.WebApp?.showAlert('Сервер удален');
      loadData();
    } catch (error) {
      console.error('Delete error:', error);
      const errorMessage = error.response?.data?.detail || error.message;
      window.Telegram?.WebApp?.showAlert(`Ошибка: ${errorMessage}`);
    }
  };

  const handleEdit = (server) => {
    setEditingServer(server);
    setFormData({
      domain: server.domain,
      name: server.name,
      country_code: server.country_code,
      subscription_base_url: server.subscription_base_url,
      is_active: server.is_active
    });
    setShowAddForm(true);
    setValidationResult(null);
  };

  const handleToggleActive = async (server) => {
    try {
      await api.patch(`/admin/servers/${server.id}`, {
        is_active: !server.is_active
      });
      loadData();
    } catch (error) {
      console.error('Toggle error:', error);
      const errorMessage = error.response?.data?.detail || error.message;
      window.Telegram?.WebApp?.showAlert(`Ошибка: ${errorMessage}`);
    }
  };

  if (loading) {
    return (
      <div className="admin-servers-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-servers-page">
      <div className="admin-header">
        <div className="admin-header-content">
          <Server size={24} />
          <h1>Управление серверами</h1>
        </div>
        <button
          className="btn-add-server"
          onClick={() => {
            setShowAddForm(true);
            setEditingServer(null);
            setFormData({
              domain: '',
              name: '',
              country_code: '',
              subscription_base_url: '',
              is_active: true
            });
            setValidationResult(null);
          }}
        >
          <Plus size={20} />
          Добавить сервер
        </button>
      </div>

      {showAddForm && (
        <div className="server-form-modal">
          <div className="server-form-card">
            <h2>{editingServer ? 'Редактировать сервер' : 'Добавить новый сервер'}</h2>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Домен</label>
                <input
                  type="text"
                  value={formData.domain}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  placeholder="ams3.uspn.io"
                  required
                />
              </div>

              <div className="form-group">
                <label>Название</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="AMS3"
                  required
                />
              </div>

              <div className="form-group">
                <label>Страна</label>
                <select
                  value={formData.country_code}
                  onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
                  required
                >
                  <option value="">Выберите страну</option>
                  {countries.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.flag_emoji} {country.name_ru}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Subscription URL</label>
                <input
                  type="url"
                  value={formData.subscription_base_url}
                  onChange={(e) => {
                    setFormData({ ...formData, subscription_base_url: e.target.value });
                    setValidationResult(null);
                  }}
                  onBlur={(e) => validateSubscriptionUrl(e.target.value)}
                  placeholder="https://ams3.uspn.io/go/test"
                  required
                />
                <button
                  type="button"
                  className="btn-validate"
                  onClick={() => validateSubscriptionUrl(formData.subscription_base_url)}
                >
                  Проверить URL
                </button>
              </div>

              {validationResult && (
                <div className={`validation-result ${validationResult.valid ? 'valid' : 'invalid'}`}>
                  {validationResult.valid ? (
                    <>
                      <CheckCircle size={20} />
                      <div>
                        <strong>URL валиден!</strong>
                        <p>Найдено ключей: {validationResult.keys_count}</p>
                        <p>Протоколы: {validationResult.protocols?.join(', ')}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <XCircle size={20} />
                      <div>
                        <strong>Ошибка валидации</strong>
                        <p>{validationResult.error}</p>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="form-group-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Активен
                </label>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingServer(null);
                    setValidationResult(null);
                  }}
                >
                  Отмена
                </button>
                <button type="submit" className="btn-save">
                  {editingServer ? 'Сохранить' : 'Добавить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="servers-list">
        {servers.length === 0 ? (
          <div className="no-servers">
            <Server size={48} />
            <p>Нет серверов</p>
          </div>
        ) : (
          servers.map(server => (
            <div key={server.id} className={`server-card ${!server.is_active ? 'inactive' : ''}`}>
              <div className="server-card-header">
                <div className="server-info">
                  <Globe size={20} />
                  <div>
                    <h3>{server.name}</h3>
                    <p className="server-domain">{server.domain}</p>
                  </div>
                </div>
                <div className="server-actions">
                  <button
                    className="btn-icon"
                    onClick={() => handleEdit(server)}
                    title="Редактировать"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    className="btn-icon"
                    onClick={() => handleDelete(server.id)}
                    title="Удалить"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              <div className="server-details">
                <div className="server-detail-item">
                  <span className="label">Страна:</span>
                  <span className="value">{server.country_flag} {server.country_name}</span>
                </div>
                <div className="server-detail-item">
                  <span className="label">Ключей:</span>
                  <span className="value">{server.keys_count || 'N/A'}</span>
                </div>
                <div className="server-detail-item">
                  <span className="label">Протоколы:</span>
                  <span className="value">{server.protocols?.join(', ') || 'N/A'}</span>
                </div>
              </div>

              <div className="server-url">
                <Info size={14} />
                <span>{server.subscription_base_url}</span>
              </div>

              <div className="server-footer">
                <span className="server-date">
                  Добавлен: {new Date(server.created_at).toLocaleDateString('ru-RU')}
                </span>
                <button
                  className={`btn-toggle-active ${server.is_active ? 'active' : 'inactive'}`}
                  onClick={() => handleToggleActive(server)}
                >
                  {server.is_active ? (
                    <>
                      <CheckCircle size={16} />
                      Активен
                    </>
                  ) : (
                    <>
                      <XCircle size={16} />
                      Неактивен
                    </>
                  )}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AdminServers;
